import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMDCockpit } from '../../../hooks/useMDCockpit';
import { useCesium } from '../../../context/CesiumContext';
import useNavigation from '../../../hooks/useNavigation';
import MissionService from '../../../../../../service/MissionService';
import { showToastMessage, preventKeyInputs, validateNumberWithoutDecimal } from '../../../../../../utils/commonFunctions';
import type { MissionStore } from '../../../../../../store/MissionSlice';
import { applyOrbitReportUpdatesToTreeItems, mapApiSatToTreeItem } from '../../../lib/missionSimulationMapper';
import ModelConfigDatetimeInput from './ModelConfigDatetimeInput';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import {
  getUtcNowDisplay,
  displayToEpoch,
  validateUtcDisplay,
  isCompleteUtcDisplay,
  epochToDisplay,
} from '../../../lib/datetime';
import {
  DEFAULT_PROPAGATION_STEP_SECONDS_CONFIG,
  getDefaultPropagationWindow,
  isPropagationConfigured,
} from '../../../lib/modelDefaults';
import styles from './ModelConfigModal.module.scss';

export { epochToDisplay as fromEpoch };

// ─── Icons ────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="m18 6-12 12M6 6l12 12" />
  </svg>
);

const ModelConfigIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
    <path d="M14 8C14 6.81337 13.6481 5.65339 12.9889 4.66672C12.3297 3.68005 11.3927 2.91102 10.2964 2.45685C9.20013 2.00269 7.9938 1.88379 6.82995 2.11519C5.6661 2.3466 4.597 2.91791 3.75783 3.75689C2.91867 4.59587 2.34712 5.66485 2.11545 6.82864C1.88379 7.99244 2.00242 9.1988 2.45634 10.2952C2.91026 11.3916 3.67909 12.3287 4.66561 12.9882C5.65213 13.6476 6.81204 13.9997 7.99867 14M12.6673 14C12.3137 14 11.9746 13.8595 11.7245 13.6095C11.4745 13.3594 11.334 13.0203 11.334 12.6667C11.334 12.313 11.4745 11.9739 11.7245 11.7239C11.9746 11.4738 12.3137 11.3333 12.6673 11.3333M12.6673 14C13.021 14 13.3601 13.8595 13.6101 13.6095C13.8602 13.3594 14.0007 13.0203 14.0007 12.6667C14.0007 12.313 13.8602 11.9739 13.6101 11.7239C13.3601 11.4738 13.021 11.3333 12.6673 11.3333M12.6673 14V15M12.6673 11.3333V10.3333M14.688 11.5L13.822 12M11.5133 13.3333L10.6467 13.8333M10.6467 11.5L11.5133 12M13.822 13.3333L14.6887 13.8333" stroke="currentColor" strokeWidth="0.853333" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 4.66675V8.00008L9.33333 9.33341" stroke="currentColor" strokeWidth="0.853333" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.55, cursor: 'pointer' }}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const PlanetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.0011 10C2.82477 12.3304 5.04725 14 7.65969 14C10.2721 14 12.4946 12.3304 13.3183 10C13.5296 9.40218 13.6488 8.76087 13.659 8.09311C13.6595 8.06213 13.6597 8.03109 13.6597 8C13.6597 4.68629 10.9734 2 7.65969 2C4.34598 2 1.65969 4.68629 1.65969 8M1.65969 8C2.32635 8.66667 4.59998 9.80001 6.99998 9.00001C9.99998 8.00001 9.50039 7 13.659 8.09311M1.65969 8C1.65969 8.70127 1.78 9.37444 2.0011 10M2.0011 10C2.72063 10.5 4.99998 11.4 6.99998 11C9.49998 10.5 10.0004 8.5 13.3183 10"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DragIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clipPath="url(#clip0_drag)">
      <path d="M9.33398 11.5003C9.62436 11.5003 9.91045 11.5692 10.1689 11.7015C10.4275 11.8338 10.6515 12.0255 10.8213 12.2611C10.9911 12.4967 11.1027 12.7698 11.1465 13.057C11.1903 13.3441 11.1651 13.6379 11.0732 13.9134C10.9814 14.1888 10.8254 14.4386 10.6182 14.6419C10.4109 14.8453 10.1581 14.9967 9.88086 15.0833C9.60366 15.1699 9.30965 15.1892 9.02344 15.14C8.73728 15.0907 8.4667 14.9743 8.23438 14.8001C8.01348 14.6345 7.96815 14.3208 8.13379 14.0999C8.29947 13.879 8.61307 13.8346 8.83398 14.0003C8.9395 14.0794 9.06243 14.1322 9.19238 14.1546C9.32234 14.177 9.45615 14.1685 9.58203 14.1292C9.70807 14.0899 9.82372 14.0205 9.91797 13.9281C10.0121 13.8356 10.0833 13.7222 10.125 13.597C10.1668 13.4717 10.1781 13.3379 10.1582 13.2074C10.1383 13.0769 10.0869 12.9531 10.0098 12.846C9.93257 12.739 9.83135 12.6513 9.71387 12.5911C9.59634 12.531 9.46601 12.5003 9.33398 12.5003H1.33398C1.05795 12.5003 0.834161 12.2763 0.833984 12.0003C0.833984 11.7242 1.05784 11.5003 1.33398 11.5003H9.33398ZM13.1953 5.50911C13.5631 5.5423 13.9166 5.66823 14.2217 5.8763C14.5268 6.08436 14.7733 6.36732 14.9385 6.69759C15.1036 7.02791 15.1816 7.39507 15.165 7.764C15.1484 8.13295 15.0379 8.49182 14.8438 8.80599C14.6496 9.12008 14.3782 9.3791 14.0557 9.55892C13.7332 9.73871 13.3702 9.83328 13.001 9.83333H1.33398C1.05784 9.83333 0.833985 9.60947 0.833984 9.33333C0.833984 9.05719 1.05784 8.83333 1.33398 8.83333H13.001C13.1996 8.83328 13.3949 8.78251 13.5684 8.68587C13.7421 8.58903 13.8886 8.44879 13.9932 8.27962C14.0976 8.11053 14.1571 7.91761 14.166 7.71907C14.1749 7.52041 14.1329 7.32272 14.0439 7.14486C13.955 6.96704 13.8225 6.81449 13.6582 6.70247C13.4939 6.59043 13.3035 6.52209 13.1055 6.50423C12.9074 6.48637 12.7081 6.52006 12.5264 6.60091C12.3447 6.68176 12.1867 6.80804 12.0674 6.96712C11.9016 7.18781 11.588 7.23235 11.3672 7.06673C11.1463 6.90103 11.1019 6.58743 11.2676 6.36654C11.4892 6.07118 11.7828 5.83696 12.1201 5.68685C12.4574 5.53683 12.8276 5.47596 13.1953 5.50911ZM6.16699 1.33333C6.16699 1.0573 6.391 0.833508 6.66699 0.833332C6.94313 0.833332 7.16699 1.05719 7.16699 1.33333V5.45931L8.98047 3.64681C9.17573 3.45155 9.49224 3.45155 9.6875 3.64681C9.8826 3.84208 9.88271 4.15863 9.6875 4.35384L7.02051 7.01986C6.82525 7.21512 6.50874 7.21512 6.31348 7.01986L3.64746 4.35384C3.4522 4.15858 3.4522 3.84207 3.64746 3.64681C3.84272 3.45155 4.15923 3.45155 4.35449 3.64681L6.16699 5.45931V1.33333Z" fill="currentColor" />
    </g>
    <defs>
      <clipPath id="clip0_drag">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const SrpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3.19922 14.0193C3.42002 14.0193 3.59943 14.1979 3.59961 14.4187C3.59961 14.6396 3.42013 14.8191 3.19922 14.8191H1.99902C1.77819 14.819 1.59961 14.6395 1.59961 14.4187C1.59979 14.198 1.77831 14.0194 1.99902 14.0193H3.19922ZM9.99902 14.4187C9.99893 13.8884 9.78808 13.3796 9.41309 13.0046C9.03804 12.6297 8.52934 12.4187 7.99902 12.4187C7.46869 12.4187 6.95998 12.6296 6.58496 13.0046C6.20997 13.3796 5.99911 13.8884 5.99902 14.4187C5.99902 14.6395 5.82034 14.8189 5.59961 14.8191C5.3787 14.8191 5.19922 14.6396 5.19922 14.4187C5.19931 13.6762 5.49451 12.9642 6.01953 12.4392C6.54458 11.9142 7.25652 11.6189 7.99902 11.6189C8.74163 11.6189 9.45439 11.9141 9.97949 12.4392C10.5044 12.9642 10.7987 13.6763 10.7988 14.4187C10.7988 14.6395 10.6202 14.819 10.3994 14.8191C10.1785 14.8191 9.99902 14.6396 9.99902 14.4187ZM13.999 14.0193C14.2198 14.0193 14.3992 14.1979 14.3994 14.4187C14.3994 14.6396 14.2199 14.8191 13.999 14.8191H12.7988C12.5781 14.8189 12.3994 14.6395 12.3994 14.4187C12.3996 14.198 12.5782 14.0195 12.7988 14.0193H13.999ZM3.47461 9.89426C3.63083 9.73812 3.88385 9.73808 4.04004 9.89426L4.88574 10.74C5.04193 10.8962 5.04188 11.1492 4.88574 11.3054C4.72953 11.4616 4.47652 11.4616 4.32031 11.3054L3.47461 10.4597C3.3184 10.3035 3.3184 10.0505 3.47461 9.89426ZM11.958 9.89426C12.1142 9.73805 12.3682 9.73806 12.5244 9.89426C12.6803 10.0504 12.6803 10.3035 12.5244 10.4597L11.6777 11.3054C11.5215 11.4616 11.2685 11.4616 11.1123 11.3054C10.9563 11.1492 10.9562 10.8961 11.1123 10.74L11.958 9.89426ZM7.59961 10.2195V9.10423C7.59967 8.88342 7.77823 8.70491 7.99902 8.70481C8.2199 8.70481 8.39936 8.88336 8.39941 9.10423V10.2195C8.39916 10.4402 8.21978 10.6189 7.99902 10.6189C7.77835 10.6188 7.59987 10.4401 7.59961 10.2195ZM2.19629 2.23313C2.38362 2.11605 2.62998 2.17275 2.74707 2.36008C2.86415 2.54741 2.80742 2.79377 2.62012 2.91087C1.97038 3.31695 1.80859 3.62999 1.80859 3.822C1.80866 4.01402 1.97051 4.32712 2.62012 4.73313C3.30364 5.16036 3.80859 5.68072 3.80859 6.322C3.80853 6.96326 3.30365 7.48365 2.62012 7.91087C2.43284 8.02791 2.18646 7.97111 2.06934 7.78391C1.95228 7.59662 2.00907 7.35025 2.19629 7.23313C2.8459 6.82712 3.00775 6.51402 3.00781 6.322C3.00781 6.12999 2.84603 5.81695 2.19629 5.41087C1.51275 4.98365 1.00787 4.46326 1.00781 3.822C1.00781 3.18072 1.51277 2.66036 2.19629 2.23313ZM13.3779 1.9802C13.5653 1.86312 13.8126 1.9208 13.9297 2.10813C14.0465 2.29543 13.989 2.5419 13.8018 2.65891C13.1522 3.06492 12.9902 3.37806 12.9902 3.57005C12.9903 3.76202 13.1527 4.07441 13.8018 4.4802C14.4854 4.90745 14.9902 5.42872 14.9902 6.07005C14.9901 6.71128 14.4853 7.23171 13.8018 7.65891C13.6145 7.77577 13.368 7.71916 13.251 7.53196C13.1339 7.34462 13.1906 7.09729 13.3779 6.9802C14.0274 6.57425 14.1903 6.26207 14.1904 6.07005C14.1904 5.87803 14.0277 5.565 13.3779 5.15891C12.6945 4.73174 12.1905 4.21119 12.1904 3.57005C12.1904 2.92872 12.6943 2.40745 13.3779 1.9802ZM7.78711 1.02122C7.97444 0.904133 8.22178 0.960836 8.33887 1.14817C8.45573 1.33547 8.39818 1.58192 8.21094 1.69895C7.56123 2.10503 7.39941 2.41808 7.39941 2.61008C7.39941 2.80209 7.56123 3.11514 8.21094 3.52122C8.89453 3.94846 9.39941 4.46876 9.39941 5.11008C9.39941 5.75141 8.89453 6.27171 8.21094 6.69895C8.02361 6.816 7.77723 6.75931 7.66016 6.572C7.54312 6.38474 7.59995 6.13836 7.78711 6.02122C8.43685 5.61513 8.59961 5.3021 8.59961 5.11008C8.59961 4.91807 8.43685 4.60504 7.78711 4.19895C7.10363 3.77173 6.59961 3.25134 6.59961 2.61008C6.59961 1.96883 7.10363 1.44844 7.78711 1.02122ZM7.78711 1.02122Z" fill="currentColor" />
  </svg>
);

const ThirdBodyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4.96151 5.62239C5.45012 5.50729 5.81512 5.79407 5.97909 6.11165C6.13383 6.41153 6.15863 6.80095 6.0123 7.139C5.83987 7.53761 5.79121 7.979 5.87265 8.4056C5.95407 8.83212 6.16131 9.22454 6.46835 9.53157C6.77537 9.83854 7.16788 10.0459 7.59433 10.1273C8.0209 10.2086 8.46238 10.1601 8.86093 9.98763C9.19899 9.84127 9.58884 9.86588 9.88925 10.0208C10.2075 10.185 10.4941 10.5498 10.3785 11.0384C10.1938 11.8177 9.80439 12.5341 9.25058 13.1126C8.69668 13.6912 7.99772 14.1119 7.22714 14.3304C6.4567 14.5488 5.64181 14.5574 4.86679 14.3558C4.09166 14.1542 3.38435 13.7492 2.81796 13.1829C2.25157 12.6166 1.84686 11.9092 1.64511 11.1341C1.44337 10.359 1.45212 9.54431 1.6705 8.77376C1.88893 8.00315 2.30879 7.3043 2.8873 6.75032C3.46566 6.19653 4.18143 5.80729 4.96054 5.62239H4.96151ZM5.10409 6.61849C4.53209 6.76993 4.00678 7.06306 3.5787 7.47298C3.12886 7.90382 2.80228 8.44695 2.63241 9.04622C2.46252 9.64558 2.45596 10.2793 2.61288 10.8822C2.7698 11.485 3.08445 12.0354 3.52499 12.4759C3.96551 12.9163 4.51589 13.2312 5.11874 13.388C5.72157 13.5448 6.35543 13.5375 6.95468 13.3675C7.55392 13.1975 8.09717 12.8712 8.52792 12.4212C8.93789 11.993 9.22913 11.467 9.38046 10.8949C9.37084 10.8932 9.36047 10.8904 9.34921 10.89C9.31308 10.8885 9.28143 10.8956 9.25839 10.9056C8.67595 11.1577 8.03122 11.2285 7.4078 11.1097C6.78428 10.9908 6.2102 10.6874 5.76132 10.2386C5.31256 9.78984 5.00922 9.21649 4.89022 8.5931C4.77122 7.96968 4.8424 7.32504 5.09433 6.74251V6.74154C5.10411 6.71891 5.11043 6.68761 5.10897 6.65169C5.10848 6.63963 5.10595 6.62867 5.10409 6.61849ZM10.1666 8.00032C10.1666 7.42581 9.93897 6.8744 9.5328 6.4681C9.12652 6.06181 8.57515 5.83338 8.00058 5.83333C7.72443 5.83333 7.50058 5.60947 7.50058 5.33333C7.50058 5.05719 7.72443 4.83333 8.00058 4.83333C8.84036 4.83338 9.64601 5.16724 10.2398 5.76107C10.8335 6.35491 11.1666 7.16059 11.1666 8.00032C11.1664 8.27632 10.9426 8.50032 10.6666 8.50032C10.3908 8.50007 10.1668 8.27616 10.1666 8.00032ZM14.6666 7.50032C14.9427 7.50032 15.1666 7.72418 15.1666 8.00032C15.1664 8.27632 14.9426 8.50032 14.6666 8.50032H13.3336C13.0576 8.50024 12.8338 8.27627 12.8336 8.00032C12.8336 7.72423 13.0575 7.5004 13.3336 7.50032H14.6666ZM12.3131 2.97982C12.5083 2.78455 12.8258 2.78455 13.0211 2.97982C13.216 3.17501 13.216 3.49165 13.0211 3.68685L12.1832 4.52376C11.9879 4.71902 11.6714 4.71902 11.4762 4.52376C11.2811 4.32848 11.281 4.01191 11.4762 3.81673L12.3131 2.97982ZM7.50058 2.66634V1.33333C7.50058 1.05719 7.72443 0.833332 8.00058 0.833332C8.27664 0.833428 8.50058 1.05725 8.50058 1.33333V2.66634C8.50058 2.94242 8.27664 3.16624 8.00058 3.16634C7.72443 3.16634 7.50058 2.94248 7.50058 2.66634ZM7.50058 2.66634Z" fill="currentColor" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

type DurationUnit = 'hours' | 'days';

const DURATION_UNIT_SECONDS: Record<DurationUnit, number> = {
  hours: 3600,
  days: 86400,
};

const durationSecondsFromInput = (value: string, unit: DurationUnit): number | null => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num * DURATION_UNIT_SECONDS[unit]);
};

const formatDurationInputValue = (seconds: number, unit: DurationUnit): string => {
  const value = seconds / DURATION_UNIT_SECONDS[unit];
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
};

const pickDurationUnit = (seconds: number): DurationUnit => {
  if (seconds > 0 && seconds % 86400 === 0) return 'days';
  return 'hours';
};

const validateStepSize = (value: string): string => {
  const num = Number(value);
  if (!value.trim()) return 'Step size is required';
  if (!Number.isFinite(num)) return 'Step size must be a valid number';
  if (num < 10 || num > 60) return 'Step size must be between 10 and 60 seconds';
  return '';
};

const validateStartTime = (time: string): string => {
  const err = validateUtcDisplay(time);
  if (err) return err;
  if (!isCompleteUtcDisplay(time)) return 'Please enter a complete date and time';

  const match = time.match(/^(\d{4})/);
  if (match) {
    const year = parseInt(match[1], 10);
    if (year < 2000 || year > 2037) {
      return 'Year must be between 2000 and 2037';
    }
  }
  return '';
};

const validateEndTime = (endTimeVal: string, startTimeVal: string): string => {
  const err = validateUtcDisplay(endTimeVal);
  if (err) return err;
  if (!isCompleteUtcDisplay(endTimeVal)) return 'Please enter a complete date and time';

  const match = endTimeVal.match(/^(\d{4})/);
  if (match) {
    const year = parseInt(match[1], 10);
    if (year < 2000 || year > 2037) {
      return 'Year must be between 2000 and 2037';
    }
  }

  const startEpoch = displayToEpoch(startTimeVal);
  const endEpoch = displayToEpoch(endTimeVal);

  if (startEpoch !== null && endEpoch !== null && endEpoch <= startEpoch) {
    return 'End time must be after start time';
  }

  return '';
};

const sanitizeDecimalInput = (val: string, maxDecimals = 8): string => {
  if (!val) return '';
  let cleaned = val.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }
  if (cleaned.includes('.')) {
    const [integerPart, decimalPart] = cleaned.split('.');
    cleaned = `${integerPart}.${decimalPart.slice(0, maxDecimals)}`;
  }
  return cleaned;
};

// ─── Component ────────────────────────────────────────────────────────────────

const ModelConfigModal: React.FC = () => {
  const { modelConfigOpen, closeModelConfig, modelData, setModelData, setSatellites, setAccessWindowStatus } = useMDCockpit();
  const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);
  const cesium = useCesium();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();
  const { goBack } = useNavigation();

  const [startTime, setStartTime] = useState(getUtcNowDisplay);
  const [endTime, setEndTime] = useState(() => {
    const { end } = getDefaultPropagationWindow();
    return end;
  });
  const [durationValue, setDurationValue] = useState('1');
  const [durationUnit, setDurationUnit] = useState<DurationUnit>('hours');
  const [stepSize, setStepSize] = useState(String(DEFAULT_PROPAGATION_STEP_SECONDS_CONFIG));
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');
  const [stepSizeError, setStepSizeError] = useState('');
  const [saving, setSaving] = useState(false);
  const lastToastTimeRef = React.useRef(0);
  const showThrottledInfoToast = (msg: string) => {
    const now = Date.now();
    if (now - lastToastTimeRef.current > 3000) {
      showToastMessage('info', msg);
      lastToastTimeRef.current = now;
    }
  };

  const handlePeriodKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!preventKeyInputs(e, ['e', 'E', '+', '-'])) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const diff = e.key === 'ArrowUp' ? 1 : -1;
      const currentVal = parseFloat(durationValue) || 0;
      let newVal = currentVal + diff;
      if (newVal < 0) newVal = 0;

      const formatted = String(parseFloat(newVal.toFixed(8)));
      setDurationValue(formatted);

      const seconds = durationSecondsFromInput(formatted, durationUnit);
      const startEpoch = displayToEpoch(startTime);
      if (startEpoch !== null && seconds !== null && seconds > 0) {
        const nextEndDisplay = epochToDisplay(startEpoch + seconds);
        setEndTime(nextEndDisplay);
        const endErr = validateEndTime(nextEndDisplay, startTime);
        setEndTimeError(endErr);
      }
    }
  };

  const handleStepSizeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!preventKeyInputs(e, ['e', 'E', '+', '-', '.'])) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const diff = e.key === 'ArrowUp' ? 1 : -1;
      const currentVal = parseInt(stepSize, 10) || 0;
      let newVal = currentVal + diff;
      if (newVal < 10) newVal = 10;
      if (newVal > 60) newVal = 60;

      const stepStr = String(newVal);
      setStepSize(stepStr);

      const error = validateStepSize(stepStr);
      setStepSizeError(error);
    }
  };

  const [forces, setForces] = useState({
    harmonics: true,
    drag: false,
    srp: false,
    thirdBody: false,
  });

  const [harmonicsOrder, setHarmonicsOrder] = useState<'J2' | 'J4'>('J2');

  const [thirdBodyBodies, setThirdBodyBodies] = useState({
    sun: true,
    moon: true,
  });
  const [thirdBodyExpanded, setThirdBodyExpanded] = useState(false);

  // Forced mode: propagation not configured (null, invalid, or all-zero defaults)
  const prop = modelData?.propagation as any;
  const forced = !modelData?.perturbationForces || !isPropagationConfigured(prop);

  const syncDurationFromEpochs = (startEpoch: number, endEpoch: number) => {
    const seconds = endEpoch - startEpoch;
    if (seconds <= 0) return;
    const nextUnit = pickDurationUnit(seconds);
    setDurationUnit(nextUnit);
    setDurationValue(formatDurationInputValue(seconds, nextUnit));
  };

  const setEndFromDuration = (nextStartTime: string, nextDurationValue: string, nextDurationUnit: DurationUnit) => {
    const startEpoch = displayToEpoch(nextStartTime);
    const seconds = durationSecondsFromInput(nextDurationValue, nextDurationUnit);
    if (startEpoch === null || seconds === null) return;
    setEndTime(epochToDisplay(startEpoch + seconds));
    setEndTimeError('');
  };

  // Pre-populate from existing data or reset to defaults on open
  useEffect(() => {
    if (!modelConfigOpen) return;
    const prop = modelData?.propagation as any;
    const pf = modelData?.perturbationForces as any;

    if (isPropagationConfigured(prop)) {
      setStartTime(epochToDisplay(prop.startEpoch));
      setEndTime(epochToDisplay(prop.endEpoch));
      setStepSize(String(prop.stepSizeSeconds));
      syncDurationFromEpochs(prop.startEpoch, prop.endEpoch);
    } else {
      const { start, end } = getDefaultPropagationWindow();
      setStartTime(start);
      setEndTime(end);
      setStepSize(String(DEFAULT_PROPAGATION_STEP_SECONDS_CONFIG));
      const startEpoch = displayToEpoch(start);
      const endEpoch = displayToEpoch(end);
      if (startEpoch !== null && endEpoch !== null) syncDurationFromEpochs(startEpoch, endEpoch);
    }

    if (pf) {
      const dragEnabled = pf.drag?.enabled ?? false;
      const srpEnabled = pf.srp?.enabled ?? false;
      const thirdBodyEnabled = pf.thirdBody?.enabled ?? false;
      let harmonicsEnabled = pf.harmonics?.enabled ?? true;
      let harmonicsOrderVal = pf.harmonics?.order ?? 'J2';

      if (!dragEnabled && !srpEnabled && !thirdBodyEnabled) {
        harmonicsEnabled = true;
      }

      setForces({
        harmonics: harmonicsEnabled,
        drag: dragEnabled,
        srp: srpEnabled,
        thirdBody: thirdBodyEnabled,
      });
      setHarmonicsOrder(harmonicsOrderVal);

      const defaultBodies = { sun: true, moon: true };
      setThirdBodyBodies({
        ...defaultBodies,
        ...(pf.thirdBody?.bodies ?? {})
      });
      setThirdBodyExpanded(thirdBodyEnabled);
    } else {
      setForces({ harmonics: true, drag: false, srp: false, thirdBody: false });
      setHarmonicsOrder('J2');
      setThirdBodyBodies({ sun: true, moon: true });
      setThirdBodyExpanded(false);
    }

    setStartTimeError('');
    setEndTimeError('');
  }, [modelConfigOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!modelConfigOpen) return null;

  const startComplete = isCompleteUtcDisplay(startTime);
  const endComplete = isCompleteUtcDisplay(endTime);
  const startEpochForDuration = displayToEpoch(startTime);
  const endEpochForDuration = displayToEpoch(endTime);
  const durationSeconds = startEpochForDuration !== null && endEpochForDuration !== null
    ? endEpochForDuration - startEpochForDuration
    : null;
  const enteredDurationSeconds = durationSecondsFromInput(durationValue, durationUnit);
  const stepSizeNumber = Number(stepSize);
  const stepSizeInvalid = !stepSize.trim() || !Number.isFinite(stepSizeNumber) || stepSizeNumber < 10 || stepSizeNumber > 60;

  const propagationValid =
    startComplete &&
    endComplete &&
    validateUtcDisplay(startTime) === '' &&
    validateUtcDisplay(endTime) === '' &&
    durationSeconds !== null &&
    durationSeconds > 0 &&
    enteredDurationSeconds !== null &&
    enteredDurationSeconds > 0 &&
    !stepSizeInvalid;

  const toggleForce = (key: string) => {
    if (key === 'thirdBody') {
      setThirdBodyExpanded(prev => {
        const nextExpanded = !prev;
        setForces(f => {
          const nextEnabled = nextExpanded;
          let nextBodies = thirdBodyBodies;
          if (nextEnabled && !thirdBodyBodies.sun && !thirdBodyBodies.moon) {
            nextBodies = { sun: true, moon: true };
            setThirdBodyBodies(nextBodies);
          }
          const candidateForces = {
            ...f,
            thirdBody: nextEnabled && (nextBodies.sun || nextBodies.moon)
          };
          if (!candidateForces.drag && !candidateForces.srp && !candidateForces.thirdBody) {
            return {
              ...candidateForces,
              harmonics: true
            };
          }
          return candidateForces;
        });
        return nextExpanded;
      });
    } else if (key === 'harmonics') {
      setForces(f => {
        if (f.harmonics) {
          const anyOtherEnabled = f.drag || f.srp || f.thirdBody;
          if (!anyOtherEnabled) {
            return f;
          }
          return { ...f, harmonics: false };
        } else {
          return { ...f, harmonics: true };
        }
      });
    } else {
      setForces(f => {
        const candidateForces = { ...f, [key]: !f[key as keyof typeof f] };
        if (!candidateForces.drag && !candidateForces.srp && !candidateForces.thirdBody) {
          return {
            ...candidateForces,
            harmonics: true
          };
        }
        return candidateForces;
      });
    }
  };

  const handleThirdBodyCheckboxChange = (body: 'sun' | 'moon') => {
    setThirdBodyBodies(prev => {
      const next = { ...prev, [body]: !prev[body] };
      const anyChecked = next.sun || next.moon;
      setForces(f => {
        const candidateForces = { ...f, thirdBody: anyChecked };
        if (!candidateForces.drag && !candidateForces.srp && !candidateForces.thirdBody) {
          return {
            ...candidateForces,
            harmonics: true
          };
        }
        return candidateForces;
      });
      return next;
    });
  };

  const handleSave = () => {
    const startErr = validateStartTime(startTime);
    const endErr = validateEndTime(endTime, startTime);
    const stepErr = validateStepSize(stepSize);
    const startEpoch = displayToEpoch(startTime);
    const endEpoch = displayToEpoch(endTime);

    if (!isCompleteUtcDisplay(startTime) || startErr) {
      showToastMessage('error', startErr || 'Please enter a complete Start Time (YYYY-MM-DD HH:mm:ss)');
      return;
    }
    if (!isCompleteUtcDisplay(endTime) || endErr) {
      showToastMessage('error', endErr || 'Please enter a complete End Time (YYYY-MM-DD HH:mm:ss)');
      return;
    }
    if (stepErr) {
      showToastMessage('error', stepErr);
      return;
    }
    if (startEpoch === null || endEpoch === null || endEpoch <= startEpoch) {
      showToastMessage('error', 'End Time must be after Start Time');
      return;
    }
    if (!propagationValid) {
      showToastMessage('error', 'Invalid propagation configuration');
      return;
    }

    const requestBody = {
      propagation: {
        startEpoch,
        endEpoch,
        stepSizeSeconds: stepSizeNumber,
      },
      perturbationForces: {
        harmonics: forces.harmonics
          ? { enabled: true, order: harmonicsOrder }
          : { enabled: false },
        drag: { enabled: forces.drag },
        srp: { enabled: forces.srp },
        thirdBody: forces.thirdBody
          ? { enabled: true, bodies: thirdBodyBodies }
          : { enabled: false },
      },
    };

    setSaving(true);
    MissionService.updateMissionModel(mission_id, scenario_id, requestBody)
      .then(async (res: any) => {
        setAccessWindowStatus(null);
        const updated = res?.data?.data ?? res?.data;
        setModelData({
          name: modelData?.name ?? '',
          perturbationForces: updated?.perturbationForces ?? requestBody.perturbationForces,
          propagation: updated?.propagation ?? requestBody.propagation,
        });
        const nextProp = updated?.propagation ?? requestBody.propagation;
        cesium.initClockFromModel(nextProp.startEpoch, nextProp.endEpoch);
        if (mission_id && scenario_id) {
          const [satRes, orbitRes] = await Promise.allSettled([
            MissionService.getSatelliteGroups(mission_id, scenario_id),
            MissionService.getAllSatelliteGroupOrbit(mission_id, scenario_id),
          ]);
          if (satRes.status === 'fulfilled') {
            const list: any[] = satRes.value?.data?.data ?? satRes.value?.data ?? [];
            const reports: any[] = orbitRes.status === 'fulfilled'
              ? orbitRes.value?.data?.data ?? orbitRes.value?.data ?? []
              : [];
            const mapped = Array.isArray(list) ? list.map(mapApiSatToTreeItem) : [];
            setSatellites(Array.isArray(reports) && reports.length > 0
              ? applyOrbitReportUpdatesToTreeItems(mapped, reports)
              : mapped);
          }
        }
        closeModelConfig();
      })
      .catch(() => { /* keep modal open on error */ })
      .finally(() => setSaving(false));
  };

  return (
    <div className={styles.backdrop}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {<ModelConfigIcon />}
            <span>{'Model Configuration'}</span>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.iconBtn}
              type="button"
              title="Close"
              onClick={() => forced ? goBack() : closeModelConfig()}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* Absolute Start Time */}
          <div className={styles.fieldRow}>
            <span className={styles.lbl}>Absolute Start Time</span>
            <div className={styles.inputWrapper}>
              <div className={`${styles.fieldInput} ${styles.timeInput} ${startTimeError ? styles.fieldInputError : ''}`}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <ModelConfigDatetimeInput
                    value={displayToEpoch(startTime) ?? 0}
                    disabled={saving}
                    onChange={(epoch) => {
                      if (epoch <= 0 || !Number.isFinite(epoch)) {
                        setStartTime('');
                        setStartTimeError('Please enter a complete date and time');
                        return;
                      }
                      const display = epochToDisplay(epoch);
                      setStartTime(display);
                      const startErr = validateStartTime(display);
                      setStartTimeError(startErr);
                      if (startErr) {
                        showToastMessage('error', startErr);
                        return;
                      }

                      setStartTimeError('');
                      const seconds = durationSecondsFromInput(durationValue, durationUnit);
                      if (seconds !== null && seconds > 0) {
                        const maxDurationDays = userData?.['feature-allowed']?.['missionModelMaxDuration'] || 0;
                        if (maxDurationDays > 0) {
                          const durationDays = seconds / 86400;
                          if (durationDays > maxDurationDays) {
                            showToastMessage('error', `You have reached the maximum duration limit of ${maxDurationDays} days`);
                            setStartTimeError(`Maximum duration is ${maxDurationDays} days`);
                            return;
                          }
                        }
                        const nextEndDisplay = epochToDisplay(epoch + seconds);
                        setEndTime(nextEndDisplay);
                        setEndTimeError('');
                        const endErr = validateEndTime(nextEndDisplay, display);
                        setEndTimeError(endErr);
                        if (endErr) showToastMessage('error', endErr);
                      } else {
                        const endEpoch = displayToEpoch(endTime);
                        if (endEpoch !== null && endEpoch > epoch) {
                          syncDurationFromEpochs(epoch, endEpoch);
                          setEndTimeError('');
                        } else {
                          const endErr = validateEndTime(endTime, display);
                          setEndTimeError(endErr);
                          if (endErr) showToastMessage('error', endErr);
                        }
                      }
                    }}
                  />
                </div>
                <div className={styles.timeSuffix}>UTC</div>
              </div>
            </div>
          </div>

          {/* Absolute End Time */}
          <div className={styles.fieldRow}>
            <span className={styles.lbl}>Absolute End Time</span>
            <div className={styles.inputWrapper}>
              <div className={`${styles.fieldInput} ${styles.timeInput} ${endTimeError ? styles.fieldInputError : ''}`}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <ModelConfigDatetimeInput
                    value={displayToEpoch(endTime) ?? 0}
                    disabled={saving}
                    onChange={(epoch) => {
                      if (epoch <= 0 || !Number.isFinite(epoch)) {
                        setEndTime('');
                        setEndTimeError('Please enter a complete date and time');
                        return;
                      }
                      const display = epochToDisplay(epoch);
                      setEndTime(display);
                      const endErr = validateEndTime(display, startTime);
                      setEndTimeError(endErr);
                      if (endErr) showToastMessage('error', endErr);

                      if (!endErr) {
                        const nextEndEpoch = epoch;
                        const startEpoch = displayToEpoch(startTime);
                        if (startEpoch !== null && nextEndEpoch !== null && nextEndEpoch > startEpoch) {
                          const durationSeconds = nextEndEpoch - startEpoch;
                          const maxDurationDays = userData?.['feature-allowed']?.['missionModelMaxDuration'] || 0;
                          if (maxDurationDays > 0) {
                            const durationDays = durationSeconds / 86400;
                            if (durationDays > maxDurationDays) {
                              showToastMessage('error', `You have reached the maximum duration limit of ${maxDurationDays} days`);
                              setEndTimeError(`Maximum duration is ${maxDurationDays} days`);
                              setDurationValue('');
                              setTimeout(() => {
                              }, 0);
                              return;
                            }
                          }
                        }
                        setEndTime(display);
                        setEndTimeError('');
                        if (startEpoch !== null && nextEndEpoch !== null && nextEndEpoch > startEpoch) {
                          syncDurationFromEpochs(startEpoch, nextEndEpoch);
                        }
                      }
                    }}
                  />
                </div>
                <div className={styles.timeSuffix}>UTC</div>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className={styles.fieldRow}>
            <span className={styles.lbl}>Duration</span>
            <div className={styles.inputWrapper}>
              <div className={styles.durationInputGroup}>
                <input
                  type="text"
                  value={durationValue}
                  disabled={saving}
                  onChange={(e) => {
                    const val = e.target.value;
                    const sanitized = sanitizeDecimalInput(val, 8);

                    const decimalPart = val.split('.')[1];
                    if (decimalPart && decimalPart.length > 8) {
                      showThrottledInfoToast('Maximum of 8 decimal places is allowed');
                    }

                    setDurationValue(sanitized);
                    const seconds = durationSecondsFromInput(sanitized, durationUnit);
                    const startEpoch = displayToEpoch(startTime);
                    if (startEpoch !== null && seconds !== null && seconds > 0) {
                      // Check max duration limit (converted to days)
                      const maxDurationDays = userData?.['feature-allowed']?.['missionModelMaxDuration'] || 0;
                      if (maxDurationDays > 0) {
                        const durationDays = seconds / 86400;
                        if (durationDays > maxDurationDays) {
                          showToastMessage('error', `You have reached the maximum duration limit of ${maxDurationDays} days`);
                          setDurationValue('');
                          return;
                        }
                      }
                      setDurationValue(val);
                      const nextEndDisplay = epochToDisplay(startEpoch + seconds);
                      setEndTime(nextEndDisplay);
                      setStartTimeError('');
                      setEndTimeError('');
                      const endErr = validateEndTime(nextEndDisplay, startTime);
                      setEndTimeError(endErr);
                      if (endErr) showToastMessage('error', endErr);

                    } else {
                      setDurationValue(val);
                    }
                  }}
                  onKeyDown={handlePeriodKeyDown}
                  className={styles.durationInputField}
                />
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    const nextUnit: DurationUnit = durationUnit === 'hours' ? 'days' : 'hours';
                    setDurationUnit(nextUnit);

                    const numericVal = parseFloat(durationValue) || 0;
                    const converted = nextUnit === 'days' ? numericVal / 24 : numericVal * 24;
                    const formatted = String(parseFloat(converted.toFixed(8)));
                    setDurationValue(formatted);

                    const seconds = durationSecondsFromInput(formatted, nextUnit);
                    const startEpoch = displayToEpoch(startTime);
                    if (startEpoch !== null && seconds !== null && seconds > 0) {
                      // Check max duration limit (converted to days)
                      const maxDurationDays = userData?.['feature-allowed']?.['missionModelMaxDuration'] || 0;
                      if (maxDurationDays > 0) {
                        const durationDays = seconds / 86400;
                        if (durationDays > maxDurationDays) {
                          showToastMessage('error', `You have reached the maximum duration limit of ${maxDurationDays} days`);
                          return;
                        }
                      }
                      const nextEndDisplay = epochToDisplay(startEpoch + seconds);
                      setEndTime(nextEndDisplay);
                      const endErr = validateEndTime(nextEndDisplay, startTime);
                      setEndTimeError(endErr);
                      if (endErr) showToastMessage('error', endErr);
                    }
                  }}
                  className={styles.durationUnitBtn}
                >
                  {durationUnit === 'hours' ? 'Hours' : 'Days'}
                </button>
              </div>
            </div>
          </div>

          {/* Propagation Step Size */}
          <div className={styles.fieldRow}>
            <span className={styles.lbl}>Propagation Step Size</span>
            <div className={styles.inputWrapper}>
              <div className={`${styles.stepInput} ${stepSizeError ? styles.fieldInputError : ''}`}>
                <input
                  type="text"
                  value={stepSize}
                  onChange={(e) => {
                    const val = e.target.value;
                    const sanitized = val.replace(/[^0-9]/g, '');
                    const numVal = Number(sanitized);

                    if (sanitized === '' || numVal <= 60) {
                      setStepSize(sanitized);
                      const error = validateStepSize(sanitized);
                      setStepSizeError(error);

                      const numValForToast = Number(sanitized);
                      if (sanitized.trim() && (numValForToast < 10 || numValForToast > 60)) {
                        showThrottledInfoToast('Propagation step size must be between 10 and 60 seconds');
                      }
                    } else {
                      showThrottledInfoToast('Propagation step size must be between 10 and 60 seconds');
                    }
                  }}
                  onKeyDown={handleStepSizeKeyDown}
                  onBlur={() => {
                    const error = validateStepSize(stepSize);
                    setStepSizeError(error);
                  }}
                />
                <div className={styles.stepSuffix}>s</div>
              </div>
            </div>
          </div>

          {/* Perturbation forces */}
          <div className={styles.sectionHeader}>Perturbation Forces</div>

          <div className={styles.perturbList}>
            {/* Earth Harmonics */}
            <div className={styles.perturbBox}>
              <div className={styles.perturbHeader} onClick={() => toggleForce('harmonics')}>
                <div className={styles.perturbTitle}>
                  <PlanetIcon />
                  <span>Earth Harmonics</span>
                  <CustomToolTip title="Earth's non-spherical gravity effects (e.g. J2, J4)." placement="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <InfoIcon />
                    </span>
                  </CustomToolTip>
                </div>
                <div className={styles.toggleTrack}>
                  <div className={`${styles.toggleBg} ${forces.harmonics ? styles.on : styles.off}`} />
                  <div className={`${styles.toggleThumb} ${forces.harmonics ? styles.on : styles.off}`} />
                </div>
              </div>
              {forces.harmonics && (
                <div className={styles.subRow}>
                  <span className={styles.subLabel}>Gravity Order</span>
                  <div className={styles.subOptions}>
                    {(['J2', 'J4'] as const).map(order => (
                      <label key={order} className={styles.radioRow}>
                        <input
                          type="radio"
                          name="harmonicsOrder"
                          value={order}
                          checked={harmonicsOrder === order}
                          onChange={() => setHarmonicsOrder(order)}
                        />
                        <span className={styles.radioLabel}>{order}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Atmospheric Drag */}
            <div className={styles.perturbBox}>
              <div className={styles.perturbHeader} onClick={() => toggleForce('drag')}>
                <div className={styles.perturbTitle}>
                  <DragIcon />
                  <span>Atmospheric Drag</span>
                  <CustomToolTip title="Atmospheric resistance on low-orbit satellites." placement="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <InfoIcon />
                    </span>
                  </CustomToolTip>
                </div>
                <div className={styles.toggleTrack}>
                  <div className={`${styles.toggleBg} ${forces.drag ? styles.on : styles.off}`} />
                  <div className={`${styles.toggleThumb} ${forces.drag ? styles.on : styles.off}`} />
                </div>
              </div>
            </div>

            {/* Solar Radiation Pressure */}
            <div className={styles.perturbBox}>
              <div className={styles.perturbHeader} onClick={() => toggleForce('srp')}>
                <div className={styles.perturbTitle}>
                  <SrpIcon />
                  <span>Solar Radiation Pressure</span>
                  <CustomToolTip title="Radiation force exerted by solar light." placement="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <InfoIcon />
                    </span>
                  </CustomToolTip>
                </div>
                <div className={styles.toggleTrack}>
                  <div className={`${styles.toggleBg} ${forces.srp ? styles.on : styles.off}`} />
                  <div className={`${styles.toggleThumb} ${forces.srp ? styles.on : styles.off}`} />
                </div>
              </div>
            </div>

            {/* Third Body Axis */}
            <div className={styles.perturbBox}>
              <div className={styles.perturbHeader} onClick={() => toggleForce('thirdBody')}>
                <div className={styles.perturbTitle}>
                  <ThirdBodyIcon />
                  <span>Third Body Axis</span>
                  <CustomToolTip title="Gravitational effects of the Sun and Moon." placement="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <InfoIcon />
                    </span>
                  </CustomToolTip>
                </div>
                <div className={styles.toggleTrack}>
                  <div className={`${styles.toggleBg} ${forces.thirdBody ? styles.on : styles.off}`} />
                  <div className={`${styles.toggleThumb} ${forces.thirdBody ? styles.on : styles.off}`} />
                </div>
              </div>
              {thirdBodyExpanded && (
                <div className={styles.subRow}>
                  <span className={styles.subLabel}>Bodies</span>
                  <div className={styles.subOptions}>
                    {(['sun', 'moon'] as const).map(body => (
                      <label key={body} className={styles.checkRow}>
                        <input
                          type="checkbox"
                          checked={thirdBodyBodies[body]}
                          onChange={() => handleThirdBodyCheckboxChange(body)}
                        />
                        <span className={styles.checkLabel}>
                          {body === 'sun' ? 'Sun' : 'Moon'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {!forced && (
            <button className={styles.cancelBtn} type="button" onClick={closeModelConfig} disabled={saving}>
              Cancel
            </button>
          )}
          <button className={styles.saveBtn} type="button" onClick={handleSave} disabled={saving || !propagationValid}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ModelConfigModal;
